import {
  GraphQLObjectType,
  GraphQLInt,
  GraphQLNonNull,
  GraphQLString,
  GraphQLID,
  GraphQLList,
  GraphQLScalarType
} from 'graphql'

export interface TermBucket {
  readonly count: number
  readonly key: string
}

export interface ServerBucket {
  readonly count: number
  readonly key: {
    readonly name: string
    readonly id: string
  }
}

export interface AppImportanceBucket {
  readonly count: number
  readonly key: {
    readonly label: string
    readonly id: string
  }
}

export interface ServerEnvironmentBucket {
  readonly count: number
  readonly key: {
    readonly label: string
    readonly id: string
  }
}

export interface AgentLanguageBucket {
  readonly count: number
  readonly key: {
    readonly id: string
    readonly label: string
  }
}

export interface ApplicationBucket {
  readonly count: number
  readonly key: {
    readonly id: string
    readonly name: string
  }
}

export interface ComplianceBucket {
  readonly count: number
  readonly key: {
    readonly id: string
    readonly name: string
  }
}

export interface LabeledBucket {
  readonly count: number
  readonly key: {
    readonly id: string
    readonly label: string
  }
}

export interface Subfilter {
  readonly count: number
  readonly keycode: string
  readonly id: string
  readonly label: string
}

export interface LabeledBucketWithSubfilters {
  readonly count: number
  readonly key: {
    readonly id: string
    readonly label: string
    readonly subfiltersId?: string
    readonly subfilters?: Subfilter[]
  }
}

const getNamedField = () => ({
  name: {
    type: new GraphQLNonNull(GraphQLString)
  },
  id: {
    type: new GraphQLNonNull(GraphQLString)
  }
})

const getLabeldField = () => ({
  label: {
    type: new GraphQLNonNull(GraphQLString)
  },
  id: {
    type: new GraphQLNonNull(GraphQLString)
  }
})

function bucketType({
  name,
  keyType,
  isCountRequired = true
}: {
  readonly name: string
  readonly keyType: GraphQLScalarType | GraphQLObjectType
  readonly isCountRequired?: boolean
}) {
  return new GraphQLObjectType({
    name,
    description:
      'A bucket represents a set of entities associated with a particular criterion. An entity falls into a bucket if it matches the criterion - e.g. an application falls into a bucket for the tag "foo" if that application has been tagged with "foo"',
    fields: () => ({
      count: {
        description: 'The number of entities that fall into this bucket.',
        type: isCountRequired ? new GraphQLNonNull(GraphQLInt) : GraphQLInt
      },
      key: {
        description:
          'The criterion that caused an entity to fall into this bucket. In some cases this is a built-in type like string, representing some term. In other cases it might be a more rich data type.',
        type: new GraphQLNonNull(keyType)
      }
    })
  })
}

export const TermBucketType = bucketType({
  name: 'TermBucket',
  keyType: GraphQLString,
  isCountRequired: false
})

export const ServerBucketType = bucketType({
  name: 'ServerBucket',
  keyType: new GraphQLObjectType({
    name: 'ServerKey',
    description:
      'A server key contains a subset of the fields that describe a server known to Contrast.',
    fields: getNamedField
  })
})

export const AppImportanceBucketType = bucketType({
  name: 'AppImportanceBucket',
  keyType: new GraphQLObjectType({
    name: 'AppImportanceKey',
    description:
      'An app importance key contains a subset of the fields that describe the level of app importance known to Contrast.',
    fields: getLabeldField
  }),
  isCountRequired: false
})

export const ServerEnvironmentBucketType = bucketType({
  name: 'ServerEnvironmentBucket',
  keyType: new GraphQLObjectType({
    name: 'ServerEnvironmentKey',
    description:
      'A server environment key contains a subset of the fields that describe a server environment known to Contrast.',
    fields: getLabeldField
  }),
  isCountRequired: false
})

export const AgentLanguageBucketType = bucketType({
  name: 'AgentLanguageBucket',
  keyType: new GraphQLObjectType({
    name: 'AgentLanguageKey',
    description:
      'An agent language key contains a subset of the fields that describe an agent language known to Contrast.',
    fields: getLabeldField
  }),
  isCountRequired: false
})

export const ApplicationBucketType = bucketType({
  name: 'ApplicationBucket',
  keyType: new GraphQLObjectType({
    name: 'ApplicationKey',
    description:
      'A application key contains a subset of the fields that describe a application known to Contrast.',
    fields: getNamedField
  })
})

export const ComplianceBucketType = bucketType({
  name: 'CompliancePolicyBucket',
  keyType: new GraphQLObjectType({
    name: 'CompliancyPolicyKey',
    description:
      'A compliance policy key contains a subset of the fields that describe a Contract Compliance Policy',
    fields: getNamedField
  })
})

export const LabeledBucketType = bucketType({
  name: 'LabeledBucket',
  keyType: new GraphQLObjectType({
    name: 'LabeledKey',
    description: 'A labeled key contains a human-readable label',
    fields: getLabeldField
  }),
  isCountRequired: false
})

export const subfilterType = new GraphQLObjectType({
  name: 'Subfilter',
  description: 'A labeled key contains a human-readable label',
  fields: () => ({
    keycode: {
      type: new GraphQLNonNull(GraphQLString)
    },
    label: {
      type: new GraphQLNonNull(GraphQLString)
    },
    count: {
      type: new GraphQLNonNull(GraphQLInt)
    }
  })
})

export const LabeledBucketWithSubfiltersType = bucketType({
  name: 'LabeledBucketWithSubfilters',
  keyType: new GraphQLObjectType({
    name: 'LabeledWithSubfiltersKey',
    description: 'A labeled key contains a human-readable label',
    fields: () => ({
      label: {
        type: new GraphQLNonNull(GraphQLString)
      },
      id: {
        type: new GraphQLNonNull(GraphQLString)
      },
      subfiltersId: {
        type: GraphQLString
      },
      subfilters: {
        type: new GraphQLList(subfilterType)
      }
    })
  }),
  isCountRequired: false
})

export interface CustomMetadataBuckets {
  readonly id: string
  readonly fieldType: string
  readonly label: string
  readonly buckets: Array<{
    readonly count: number
    readonly key: {
      readonly id: string
      readonly value: string
      readonly subfieldValues?: Array<{
        readonly fieldType: string
        readonly value: string
      }>
    }
  }>
}

export const CustomMetadataBucketsType = new GraphQLObjectType({
  name: 'CustomMetadataBuckets',
  description: 'A set of buckets that represent custom metadata fields',
  fields: () => ({
    id: {
      description: 'A unique ID for the metadata field',
      type: new GraphQLNonNull(GraphQLID)
    },
    fieldType: {
      description: 'The type of metadata field',
      type: new GraphQLNonNull(GraphQLString)
    },
    label: {
      description: 'The human-readable label for the field',
      type: new GraphQLNonNull(GraphQLString)
    },
    buckets: {
      description:
        'The set of buckets that represent values and counts for this particular metadata group',
      type: new GraphQLNonNull(
        new GraphQLList(
          new GraphQLNonNull(
            bucketType({
              name: 'CustomMetadataBucket',
              keyType: new GraphQLObjectType({
                name: 'CustomMetadataKey',
                description:
                  'A custom metadata key contains a value and potentially a list of subfield values',
                fields: () => ({
                  id: {
                    description:
                      'A unique identifying string for the metadata value',
                    type: new GraphQLNonNull(GraphQLID)
                  },
                  value: {
                    description: 'The value of the metadata field',
                    type: new GraphQLNonNull(GraphQLString)
                  },
                  subfieldValues: {
                    description: 'Optional subfield values',
                    type: new GraphQLList(
                      new GraphQLObjectType({
                        name: 'CustomMetadataSubfieldValue',
                        description:
                          'A custom metadata field and value with a parent field',
                        fields: () => ({
                          fieldType: {
                            description: 'The type of metadata field',
                            type: new GraphQLNonNull(GraphQLString)
                          },
                          value: {
                            description: 'The value of the metadata field',
                            type: new GraphQLNonNull(GraphQLString)
                          }
                        })
                      })
                    )
                  }
                })
              })
            })
          )
        )
      )
    }
  })
})

export interface SessionMetadataBuckets {
  readonly id: string
  readonly fieldType: string
  readonly label: string
  readonly buckets: Array<{
    readonly count: number
    readonly key: {
      readonly id: string
      readonly value: string
    }
  }>
}

export const SessionMetadataBucketsType = new GraphQLObjectType({
  name: 'SessionMetadataBuckets',
  description: 'A set of buckets that represent session metadata fields',
  fields: () => ({
    id: {
      description: 'A unique ID for the metadata field',
      type: new GraphQLNonNull(GraphQLID)
    },
    fieldType: {
      description: 'The type of metadata field',
      type: new GraphQLNonNull(GraphQLString)
    },
    label: {
      description: 'The human-readable label for the field',
      type: new GraphQLNonNull(GraphQLString)
    },
    buckets: {
      description:
        'The set of buckets that represent values and counts for this particular metadata group',
      type: new GraphQLNonNull(
        new GraphQLList(
          new GraphQLNonNull(
            bucketType({
              name: 'SessionMetadataBucket',
              keyType: new GraphQLObjectType({
                name: 'SessionMetadataKey',
                description: 'A session metadata key contains a value',
                fields: () => ({
                  id: {
                    description:
                      'A unique identifying string for the metadata value',
                    type: new GraphQLNonNull(GraphQLID)
                  },
                  value: {
                    description: 'The value of the metadata field',
                    type: new GraphQLNonNull(GraphQLString)
                  }
                })
              })
            })
          )
        )
      )
    }
  })
})
